import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { authClient } from '@lib/betterAuth';
import { getLastLoginEmail, setLastLoginEmail } from '@lib/lastLoginEmail';
import { BrandAuthShell } from './brand/BrandAuthShell';
import { LoginForm } from './brand/LoginForm';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithEmail } = useAuth();
  const location = useLocation();
  const prefillEmail = useMemo(() => {
    const state: unknown = location.state;
    const stateEmail =
      typeof state === 'object' &&
      state !== null &&
      'email' in state &&
      typeof state.email === 'string'
        ? state.email
        : undefined;
    if (stateEmail) return stateEmail;
    try {
      const params = new URLSearchParams(location.search);
      const emailParam = params.get('email') || params.get('username');
      if (emailParam) return emailParam;
    } catch {}
    const saved = getLastLoginEmail();
    if (saved) return saved;
    return '';
  }, [location.state, location.search]);

  const handleResendVerification = useCallback(async () => {
    if (!unverifiedEmail) return;
    const result = await authClient.sendVerificationEmail({ email: unverifiedEmail });
    if (result.error) {
      throw new Error(result.error.message || 'Failed to send verification email');
    }
  }, [unverifiedEmail]);

  const handleLogin = async (username: string, password: string): Promise<boolean> => {
    setError('');
    setInvalidCredentials(false);
    setEmailNotVerified(false);
    setLoading(true);

    try {
      const result = await signInWithEmail(username, password);

      if (!result.success) {
        if (result.emailNotVerified) {
          setEmailNotVerified(true);
          setUnverifiedEmail(username);
        } else if (result.status === 401) {
          // Wrong email or password: show the page's own copy. Every other
          // failure (423 lockout, 429, network) keeps the auth layer's text.
          setInvalidCredentials(true);
        } else {
          setError(result.error || t('auth.loginFailed'));
        }
        return false;
      }
      setLastLoginEmail(username);

      return true;
    } catch {
      setError(t('error.unknown'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const stations = [
    t('auth.login.station.ordersIn'),
    t('auth.login.station.pickList'),
    t('auth.login.station.routes'),
    t('auth.login.station.driversOut'),
    t('auth.login.station.delivered'),
  ];

  return (
    <BrandAuthShell
      hero={
        <>
          <h1 className="fc-h1">{t('auth.login.heroTitle')}</h1>
          <p className="fc-lead">{t('auth.login.heroLead')}</p>
          <ol className="fc-stations" aria-label={t('auth.login.stationsLabel')}>
            {stations.map((label, index) => {
              const isEnd = index === 0 || index === stations.length - 1;
              return (
                <li key={label}>
                  <span className="fc-rail">
                    <span className={isEnd ? 'fc-ring fc-ring--filled' : 'fc-ring'} />
                    {index < stations.length - 1 ? <span className="fc-rule" /> : null}
                  </span>
                  <span className="fc-station-label">{label}</span>
                </li>
              );
            })}
          </ol>
        </>
      }
    >
      <LoginForm
        onLogin={handleLogin}
        onResendVerification={handleResendVerification}
        onEmailChange={emailNotVerified ? () => setEmailNotVerified(false) : undefined}
        error={error}
        invalidCredentials={invalidCredentials}
        emailNotVerified={emailNotVerified}
        loading={loading}
        initialUsername={prefillEmail}
      />
      {/* Registration is invitation-only — no public sign-up link */}
      <p className="fc-note">
        {t('auth.login.inviteOnly')}{' '}
        <a href={t('auth.login.requestAccessHref')}>{t('auth.login.requestAccess')}</a>
      </p>
    </BrandAuthShell>
  );
};

export default Login;
